import React from "react";
import { BrowserRouter, Routes as RouterRoutes, Route, useLocation } from "react-router-dom";

import ErrorBoundary from "./components/ErrorBoundary";
import ScrollToTop from "./components/ScrollToTop";
import ProtectedRoute from "./components/ProtectedRoute";

// Import all existing pages
import NotFound from "./pages/NotFound";

import POEManagementHub from "./pages/poe-management-hub";
import TimetableManagement from "./pages/timetable-management";
import RoleBasedDashboard from "./pages/role-based-dashboard";
import StudentServicesPortal from "./pages/student-services-portal";
import StudentRequests from "./pages/student-requests";

import ResourceServiceBooking from "./pages/resource-service-booking";

// Import authentication pages
import Login from "./pages/auth/Login";
import ChangePassword from "./pages/auth/ChangePassword";

// Import new assessment management pages
import FormativeAssessmentManagement from "./pages/formative-assessment-management";
import SummativeAssessmentPortal from "./pages/summative-assessment-portal";
import SummativeAssessmentScheduleManagement from "./pages/summative-assessment-schedule-management/index.jsx";

// Import Student Finance
import StudentFinancePortal from "./pages/student-finance";

// Import new user management page
import EnhancedUserManagementSystem from "./pages/enhanced-user-management-system";

import InstitutionHierarchyManagement from "./pages/institution-hierarchy-management";
import Faculty from "./pages/institution-hierarchy-management/Faculty";
import Department from "./pages/institution-hierarchy-management/Department";
import Section from "./pages/institution-hierarchy-management/Section";
import ProgramUnitManagementSystem from "./pages/program-unit-management-system";
import AdministrativeWorkflowCenter from "./pages/administrative-workflow-center";
import AcademicManagement from "./pages/academic-management";
import FinanceDashboard from "./pages/finance";
import SystemSettings from "./pages/role-based-dashboard/components/SystemSettings";
import ExecutiveDashboard from "./pages/role-based-dashboard/components/ExecutiveDashboard";
import { useAuth } from "./contexts/AuthContext";

// Student Management
import { StudentList, StudentForm, StudentDetail } from "./pages/students";
import StudentDashboard from "./pages/role-based-dashboard/components/StudentDashboard";
import HostelReservationPortal from "./pages/hostel/HostelReservationPortal";
import NonTeachingStaffDashboard from "./pages/non-teaching-dashboard";
import TransportPortal from "./pages/transport/TransportPortal";

import EnrollmentForm from "./pages/public/EnrollmentForm";
import ApplicationTracker from "./pages/public/ApplicationTracker";
import AdmissionsManagement from "./pages/admissions";

const EXECUTIVE_ROLES = [
  "chief_principal",
  "vice_chancellor",
  "vc",
  "principal",
];
const SYSTEM_ADMIN = ["system_administrator", ...EXECUTIVE_ROLES];
const ACADEMIC_MANAGERS = [
  "system_administrator",
  ...EXECUTIVE_ROLES,
  "deputy_principal_academic",
  "registrar_academic",
  "dean_of_faculty",
  "head_of_department",
  "head_of_section",
];
const ASSESSMENT_ROLES = [
  "system_administrator",
  ...EXECUTIVE_ROLES,
  "assessment_officer",
  "quality_assurance_officer",
  "poe_manager",
  "head_of_department",
  "head_of_section",
  "trainer_lecturer",
];
const NON_TEACHING_ROLES = [
  "system_administrator",
  ...EXECUTIVE_ROLES,
  "deputy_principal_administration",
  "dean_of_students",
  "estates_officer",
  "hostel_officer",
  "medical_officer",
  "catering_officer",
  "stores_officer",
  "procurement_officer",
  "transport_officer",
];
const HOSTEL_STAFF_ROLES = NON_TEACHING_ROLES.filter(
  (role) => role !== "transport_officer",
);
const TRANSPORT_ACCESS_ROLES = [
  ...NON_TEACHING_ROLES,
  ...EXECUTIVE_ROLES,
  "deputy_principal_academic",
  "registrar_academic",
  "dean_of_faculty",
  "head_of_department",
  "head_of_section",
  "poe_manager",
  "timetable_master",
  "assessment_officer",
  "quality_assurance_officer",
  "finance_officer",
  "trainer_lecturer",
];
const OPERATIONS_PORTAL_ROLES = [
  ...new Set([...NON_TEACHING_ROLES, ...TRANSPORT_ACCESS_ROLES]),
];

// Keep visited dashboard pages mounted while navigating between portal tabs.
// This preserves their loaded data, filters, and unsaved local form state
// instead of remounting each page into a full loading screen. Public routes
// are deliberately excluded and the cache is cleared whenever the account
// changes so private data cannot leak between sessions.
const PUBLIC_ROUTE_PATHS = new Set([
  "/login",
  "/auth/change-password",
  "/change-password",
  "/apply",
  "/track-application",
]);

const PersistentRouterRoutes = ({ children }) => {
  const location = useLocation();
  const { user, loading: authLoading } = useAuth();
  const cacheKey = location.pathname;
  const locationStateKey = `${location.pathname}${location.search}${location.hash}`;
  const isPublicRoute = PUBLIC_ROUTE_PATHS.has(location.pathname);
  const routeChildren = React.useMemo(() => React.Children.toArray(children), [children]);
  const cachedUserId = React.useRef(user?.id || null);
  const [visitedRoutes, setVisitedRoutes] = React.useState(() => (
    !authLoading && user && !isPublicRoute
      ? [{ cacheKey, location, locationStateKey }]
      : []
  ));

  React.useEffect(() => {
    if (authLoading || !user || isPublicRoute) {
      cachedUserId.current = null;
      setVisitedRoutes([]);
      return;
    }

    if (cachedUserId.current !== user.id) {
      cachedUserId.current = user.id;
      setVisitedRoutes([{ cacheKey, location, locationStateKey }]);
      return;
    }

    setVisitedRoutes(previous => {
      const existing = previous.find(route => route.cacheKey === cacheKey);
      if (existing?.locationStateKey === locationStateKey) return previous;
      if (existing) {
        return previous.map(route => route.cacheKey === cacheKey
          ? { ...route, location, locationStateKey }
          : route
        );
      }
      return [...previous, { cacheKey, location, locationStateKey }];
    });
  }, [authLoading, user?.id, isPublicRoute, cacheKey, locationStateKey]);

  if (authLoading || !user || isPublicRoute) {
    return <RouterRoutes location={location}>{routeChildren}</RouterRoutes>;
  }

  // Include the current route immediately on the first authenticated render;
  // the effect above then records it for subsequent navigations.
  const routesToRender = visitedRoutes.some(route => route.cacheKey === cacheKey)
    ? visitedRoutes
    : [...visitedRoutes, { cacheKey, location, locationStateKey }];

  return (
    <div className="contents">
      {routesToRender.map(route => (
        <div
          key={route.cacheKey}
          className={route.cacheKey === cacheKey ? "contents" : "hidden"}
          aria-hidden={route.cacheKey === cacheKey ? undefined : true}
        >
          <RouterRoutes location={route.location}>{routeChildren}</RouterRoutes>
        </div>
      ))}
    </div>
  );
};

export default function Routes() {
  return (
    <BrowserRouter>
      <ErrorBoundary>
        <ScrollToTop />
        <PersistentRouterRoutes>
          {/* Public Routes */}
          <Route path="/apply" element={<EnrollmentForm />} />
          <Route path="/track-application" element={<ApplicationTracker />} />

          <Route path="/login" element={<Login />} />
          <Route path="/auth/change-password" element={<ChangePassword />} />
          <Route path="/change-password" element={<ChangePassword />} />
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <RoleBasedDashboard />
              </ProtectedRoute>
            }
          />
          {/* Staff/student workspaces use the dashboard shell and must remain
              addressable on refresh, not fall through to the landing page. */}
          <Route
            path="/role-based-dashboard/*"
            element={
              <ProtectedRoute>
                <RoleBasedDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/executive-portal"
            element={
              <ProtectedRoute requiredRoles={EXECUTIVE_ROLES}>
                <ExecutiveDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/non-teaching-dashboard"
            element={
              <ProtectedRoute requiredRoles={OPERATIONS_PORTAL_ROLES}>
                <NonTeachingStaffDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/operations"
            element={
              <ProtectedRoute requiredRoles={OPERATIONS_PORTAL_ROLES}>
                <NonTeachingStaffDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/transport"
            element={
              <ProtectedRoute requiredRoles={TRANSPORT_ACCESS_ROLES}>
                <TransportPortal />
              </ProtectedRoute>
            }
          />
          <Route
            path="/hostel"
            element={
              <ProtectedRoute requiredRoles={["student", ...HOSTEL_STAFF_ROLES]}>
                <HostelReservationPortal />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admissions"
            element={
              <ProtectedRoute
                requiredRoles={[
                  "system_administrator",
                  "chief_principal",
                  "deputy_principal_academic",
                  "registrar_academic",
                ]}
              >
                <AdmissionsManagement />
              </ProtectedRoute>
            }
          />
          <Route
            path="/administrative-workflow-center"
            element={
              <ProtectedRoute
                requiredRoles={["system_administrator", "head_of_department"]}
              >
                <AdministrativeWorkflowCenter />
              </ProtectedRoute>
            }
          />

          {/* Assessment Management Routes */}
          <Route
            path="/formative-assessment-management"
            element={
              <ProtectedRoute requiredRoles={ASSESSMENT_ROLES}>
                <FormativeAssessmentManagement />
              </ProtectedRoute>
            }
          />
          <Route
            path="/summative-assessment-portal"
            element={
              <ProtectedRoute requiredRoles={[...ASSESSMENT_ROLES, "student"]}>
                <SummativeAssessmentPortal />
              </ProtectedRoute>
            }
          />
          <Route
            path="/summative-assessment-schedule-management"
            element={
              <ProtectedRoute requiredRoles={ASSESSMENT_ROLES}>
                <SummativeAssessmentScheduleManagement />
              </ProtectedRoute>
            }
          />

          <Route
            path="/academic-management"
            element={
              <ProtectedRoute requiredRoles={ACADEMIC_MANAGERS}>
                <AcademicManagement />
              </ProtectedRoute>
            }
          />
          <Route
            path="/finance"
            element={
              <ProtectedRoute
                requiredRoles={[...ACADEMIC_MANAGERS, "finance_officer"]}
              >
                <FinanceDashboard />
              </ProtectedRoute>
            }
          />

          <Route
            path="/poe-management-hub"
            element={
              <ProtectedRoute
                requiredRoles={[
                  "system_administrator",
                  "poe_manager",
                  "dean_of_faculty",
                  "head_of_department",
                  "head_of_section",
                  "trainer_lecturer",
                  "quality_assurance_officer",
                  "assessment_officer",
                  "external_poe_verifier",
                  "student",
                ]}
              >
                <POEManagementHub />
              </ProtectedRoute>
            }
          />

          <Route
            path="/student-finance/:tab"
            element={
              <ProtectedRoute requiredRoles={["student"]}>
                <StudentFinancePortal />
              </ProtectedRoute>
            }
          />
          <Route
            path="/student-finance"
            element={
              <ProtectedRoute requiredRoles={["student"]}>
                <StudentFinancePortal />
              </ProtectedRoute>
            }
          />

          <Route
            path="/timetable-management"
            element={
              <ProtectedRoute
                requiredRoles={[
                  "system_administrator",
                  "timetable_master",
                  "poe_manager",
                  "chief_principal",
                  "deputy_principal_academic",
                  "dean_of_faculty",
                  "head_of_department",
                  "head_of_section",
                  "registrar_academic",
                  "trainer_lecturer",
                  "student",
                  "quality_assurance_officer",
                ]}
              >
                <TimetableManagement />
              </ProtectedRoute>
            }
          />

              <Route
                path="/student-services-portal"
                element={
              <ProtectedRoute
                requiredRoles={[
                  "system_administrator",
                  "dean_of_students",
                  "student",
                ]}
              >
                <StudentServicesPortal />
              </ProtectedRoute>
                }
              />
              <Route
                path="/student-requests"
                element={
                  <ProtectedRoute>
                    <StudentRequests />
                  </ProtectedRoute>
                }
              />

          <Route
            path="/resource-service-booking"
            element={
              <ProtectedRoute>
                <ResourceServiceBooking />
              </ProtectedRoute>
            }
          />

          <Route
            path="/enhanced-user-management-system"
            element={
              <ProtectedRoute requiredRoles={SYSTEM_ADMIN}>
                <EnhancedUserManagementSystem />
              </ProtectedRoute>
            }
          />

          <Route
            path="/staff-management"
            element={
              <ProtectedRoute requiredRoles={SYSTEM_ADMIN}>
                <EnhancedUserManagementSystem isStaffMode={true} />
              </ProtectedRoute>
            }
          />

          <Route
            path="/institution-hierarchy-management"
            element={
              <ProtectedRoute requiredRoles={ACADEMIC_MANAGERS}>
                <InstitutionHierarchyManagement />
              </ProtectedRoute>
            }
          />

          <Route
            path="/hierarchy/faculty"
            element={
              <ProtectedRoute requiredRoles={ACADEMIC_MANAGERS}>
                <Faculty />
              </ProtectedRoute>
            }
          />

          <Route
            path="/hierarchy/department"
            element={
              <ProtectedRoute requiredRoles={ACADEMIC_MANAGERS}>
                <Department />
              </ProtectedRoute>
            }
          />

          <Route
            path="/hierarchy/section"
            element={
              <ProtectedRoute requiredRoles={ACADEMIC_MANAGERS}>
                <Section />
              </ProtectedRoute>
            }
          />

          <Route
            path="/program-unit-management-system"
            element={
              <ProtectedRoute requiredRoles={ACADEMIC_MANAGERS}>
                <ProgramUnitManagementSystem />
              </ProtectedRoute>
            }
          />

          {/* Student Management Module */}
          <Route
            path="/students"
            element={
              <ProtectedRoute requiredRoles={ACADEMIC_MANAGERS}>
                <StudentList />
              </ProtectedRoute>
            }
          />
          <Route
            path="/students/new"
            element={
              <ProtectedRoute requiredRoles={ACADEMIC_MANAGERS}>
                <StudentForm />
              </ProtectedRoute>
            }
          />
          <Route
            path="/students/:id"
            element={
              <ProtectedRoute requiredRoles={[...ACADEMIC_MANAGERS, "student"]}>
                <StudentDetail />
              </ProtectedRoute>
            }
          />
          <Route
            path="/students/:id/edit"
            element={
              <ProtectedRoute requiredRoles={ACADEMIC_MANAGERS}>
                <StudentForm />
              </ProtectedRoute>
            }
          />

          <Route
            path="/system-settings"
            element={
              <ProtectedRoute requiredRoles={SYSTEM_ADMIN}>
                <SystemSettings />
              </ProtectedRoute>
            }
          />

          {/* Catch all route */}
          <Route path="*" element={<NotFound />} />
        </PersistentRouterRoutes>
      </ErrorBoundary>
    </BrowserRouter>
  );
}
